/**
 * What the Plan & billing page shows (firm-model spec §10.2, §7; frame 15199:6383, Dan
 * 2026-10-04), derived purely so the rules are tested without rendering.
 */
import type { BillingCycle, BillingOverview, Money, PlanPrices, SeatPreview } from "@contract/firm/billing";
import type { Me, SeatSummary } from "@contract/firm/me";
import { planLabel } from "@contract/firm/plan";
import { formatDate } from "./homeModel";

export function formatMoney(money: Money | null): string {
  if (!money) return "";
  const amount = Number(money.amount) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

/** Every price the panel shows is before tax (Dan 2026-09-29); Paddle adds tax at checkout. */
export const BEFORE_TAX = "before tax";

/**
 * Which page the firm sees: a trial (or a cancelled plan that has ended) chooses a plan; a
 * live Individual or Team changes it; a cancelled plan still running ("ending") and a beta
 * firm only read it.
 */
export type BillingState = "trial" | "individual" | "team" | "ending" | "beta";

export function billingState(overview: BillingOverview, isBeta: boolean): BillingState {
  if (overview.subscribed) {
    if (overview.cancelsAt) return "ending";
    return overview.plan === "team" ? "team" : "individual";
  }
  return isBeta ? "beta" : "trial";
}

type Banner = { tone: "warning" | "danger" | "info"; text: string };

/** The page's one banner, or null (Team). The "ending" banner carries what the cancel
 * confirm used to say, now that cancelling happens in Paddle's portal. */
export function billingBanner(state: BillingState, overview: BillingOverview, me: Me, now: Date): Banner | null {
  switch (state) {
    case "trial": {
      if (!me.trialEndsAt) return { tone: "warning", text: "Choose a plan to keep using SlideBrain." };
      if (new Date(me.trialEndsAt).getTime() <= now.getTime()) {
        return { tone: "danger", text: "Your trial has ended. Choose a plan to keep using SlideBrain's AI features." };
      }
      return { tone: "warning", text: `Your trial ends on ${formatDate(me.trialEndsAt)}. Choose a plan to keep using SlideBrain.` };
    }
    case "individual":
      return { tone: "warning", text: "Add your team: shared standards, a firm library and pooled credits on Team." };
    case "ending": {
      const until = overview.cancelsAt ? `on ${formatDate(overview.cancelsAt)}` : "at the end of this billing period";
      return {
        tone: "info",
        text:
          `Your ${planLabel(overview.plan, false)} plan ends ${until}. After that, your firm drops to trial credits (AI features ` +
          "stop when the trial is over or its credits are used), and nobody new can be invited. Members keep their accounts. " +
          "To keep going, choose a plan once it ends.",
      };
    }
    case "beta":
      return { tone: "info", text: "Your firm is on the beta: SlideBrain is free for you until launch." };
    default:
      return null;
  }
}

/** A price cell: "$49/mo", "$490/yr". */
export function priceCell(money: Money | null, cycle: BillingCycle): string {
  if (!money) return "Not available";
  return `${formatMoney(money)}/${cycle === "monthly" ? "mo" : "yr"}`;
}

/** An AI credits cell: a plan's monthly credits per seat (from the overview) times `seats`;
 * null credits = an uncapped plan. */
export function creditsCell(perSeat: number | null, seats: number): string {
  return perSeat === null ? "Unlimited" : (perSeat * seats).toLocaleString("en-US");
}

/** Individual's price: what a live Individual subscription itself pays (a grandfathered
 * Individual pays its old price, Dan 2026-09-29), else the catalog's. */
export function individualPrice(overview: BillingOverview, cycle: BillingCycle): Money | null {
  const own = overview.subscribed && overview.plan !== "team" && overview.cycle === cycle ? overview.paying?.base : null;
  return own ?? overview.prices.individual[cycle];
}

/** Team's price for n seats: the base plus (n − included) extra seats. */
export function teamPrice(prices: { teamBase: PlanPrices; teamSeat: PlanPrices }, cycle: BillingCycle, seats: number, included: number): Money | null {
  const base = prices.teamBase[cycle];
  const seat = prices.teamSeat[cycle];
  if (!base || !seat || base.currency !== seat.currency) return null;
  const extra = Math.max(0, seats - included);
  return { amount: String(Number(base.amount) + extra * Number(seat.amount)), currency: base.currency };
}

/** Team's price for n seats as this firm would pay it: a live Team subscription's own base
 * and seat prices, else the catalog's. */
export function teamPriceFor(overview: BillingOverview, cycle: BillingCycle, seats: number): Money | null {
  const paying = overview.subscribed && overview.plan === "team" && overview.cycle === cycle ? overview.paying : null;
  const prices = paying?.base
    ? {
        teamBase: { ...overview.prices.teamBase, [cycle]: paying.base },
        teamSeat: { ...overview.prices.teamSeat, [cycle]: paying.seat ?? overview.prices.teamSeat[cycle] },
      }
    : overview.prices;
  return teamPrice(prices, cycle, seats, overview.includedSeats);
}

/** Seats held now: active members and pending invites (each takes a seat). */
function seatsInUse(seats: SeatSummary): number {
  return seats.members + seats.pendingInvites;
}

/** The seat box's floor: never below the included seats or the seats in use. */
export function minSeats(seats: SeatSummary, included: number): number {
  return Math.max(included, seatsInUse(seats));
}

/** Why the seat box can't go lower, when it's at the floor. */
export function floorLine(seats: SeatSummary, included: number): string {
  return `${minSeats(seats, included)} seats are the minimum: Team includes ${included}, and ${seatsInUse(seats)} are in use.`;
}

/** What a change does now: the prorated charge, or the credit for the next invoices. */
function chargeLine(preview: SeatPreview): string {
  const p = preview.prorated;
  if (!p) return "";
  return p.action === "charge"
    ? ` You'll pay ${formatMoney(p.amount)} now for the rest of this period, ${BEFORE_TAX}.`
    : ` You'll get ${formatMoney(p.amount)} credit for the rest of this period, ${BEFORE_TAX}, used on your next invoices.`;
}

/** The new price each period, after the change. */
function recurringLine(preview: SeatPreview): string {
  return preview.recurring ? ` Then ${formatMoney(preview.recurring)} each period, ${BEFORE_TAX}.` : "";
}

/** The confirm for a seat change on Team (prorated at once, Dan 2026-09-29). */
export function seatChangeLine(preview: SeatPreview): string {
  return `Changes to ${preview.seats} seats now.${chargeLine(preview)}${recurringLine(preview)}`;
}

/** The confirm for Individual → Team on the same subscription (R16, prorated at once). */
export function upgradeLine(preview: SeatPreview): string {
  return `Moves your firm to Team with ${preview.seats} seats now. Your standards, library and history stay as they are.${chargeLine(preview)}${recurringLine(preview)}`;
}
